/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { HtmlHeadInclude } from '@msdyn365-commerce/core';
import React, { useMemo } from 'react';
import { formatSchemaText } from './utils/format-schema-text';

export interface ProductListMetadataVariant {
    url: string;
    name: string;
    sku?: string;
    image?: string;
    brand?: string;
    color?: string;
    productGroupUrl: string;
    price: number;
    priceCurrency: string;
    isInStock: boolean;
    suggestedGender?: string;
}

export interface ProductListMetadataBreadcrumb {
    name: string;
    url: string;
}

export interface ProductListHeadMetadataProps {
    pageUrl: string;
    pageName: string;
    websiteName: string;
    websiteUrl: string;
    description?: string;
    totalItems: number;
    currentPage: number;
    itemsPerPage: number;
    variants: ProductListMetadataVariant[];
    breadcrumbs: ProductListMetadataBreadcrumb[];
}

const PRODUCT_GROUP_FRAGMENT = '#productgroup';

const buildProductGroupId = (productGroupUrl: string): string =>
    productGroupUrl.includes(PRODUCT_GROUP_FRAGMENT) ? productGroupUrl : `${productGroupUrl}${PRODUCT_GROUP_FRAGMENT}`;

const buildOffer = (variant: ProductListMetadataVariant) => ({
    '@type': 'Offer',
    url: variant.url,
    price: variant.price,
    priceCurrency: variant.priceCurrency,
    itemCondition: 'https://schema.org/NewCondition',
    availability: variant.isInStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
});

const buildProductListItem = (variant: ProductListMetadataVariant, position: number) => {
    const product: Record<string, unknown> = {
        '@type': 'Product',
        '@id': variant.url,
        name: variant.name,
        url: variant.url,
        isVariantOf: {
            '@type': 'ProductGroup',
            '@id': buildProductGroupId(variant.productGroupUrl)
        },
        offers: buildOffer(variant)
    };

    if (variant.brand) {
        product.brand = {
            '@type': 'Brand',
            name: variant.brand
        };
    }

    if (variant.color) {
        product.color = variant.color;
    }

    if (variant.sku) {
        product.sku = variant.sku;
    }

    if (variant.image) {
        product.image = variant.image;
    }

    if (variant.suggestedGender) {
        product.audience = {
            '@type': 'PeopleAudience',
            suggestedGender: variant.suggestedGender
        };
    }

    return {
        '@type': 'ListItem',
        position,
        item: product
    };
};

interface CollectionPageSchemaParams {
    pageUrl: string;
    pageName: string;
    websiteName: string;
    websiteUrl: string;
    totalItems: number;
    currentPage: number;
    itemsPerPage: number;
    variants: ProductListMetadataVariant[];
    formattedDescription: string | undefined;
}

const buildCollectionPageSchema = ({
    pageUrl,
    pageName,
    websiteName,
    websiteUrl,
    totalItems,
    currentPage,
    itemsPerPage,
    variants,
    formattedDescription
}: CollectionPageSchemaParams) => {
    const itemListElement = variants.map((variant, index) => buildProductListItem(variant, (currentPage - 1) * itemsPerPage + index + 1));

    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#collectionpage`,
        url: pageUrl,
        name: pageName,
        isPartOf: {
            '@type': 'WebSite',
            name: websiteName,
            url: websiteUrl
        },
        mainEntity: {
            '@type': 'ItemList',
            '@id': `${pageUrl}#itemlist`,
            numberOfItems: totalItems,
            itemListOrder: 'https://schema.org/ItemListOrderAscending',
            itemListElement
        }
    };

    if (formattedDescription) {
        schema.description = formattedDescription;
    }

    return schema;
};

const buildBreadcrumbSchema = (breadcrumbs: ProductListMetadataBreadcrumb[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url
    }))
});

const ProductListHeadMetadata: React.FC<ProductListHeadMetadataProps> = props => {
    const { pageUrl, pageName, websiteName, websiteUrl, description, totalItems, currentPage, itemsPerPage, variants, breadcrumbs } = props;

    const collectionPageSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                pageUrl,
                pageName,
                websiteName,
                websiteUrl,
                totalItems,
                currentPage,
                itemsPerPage,
                variants,
                formattedDescription: formatSchemaText(description)
            }),
        [pageUrl, pageName, websiteName, websiteUrl, description, totalItems, currentPage, itemsPerPage, variants]
    );

    const breadcrumbSchema = useMemo(() => (breadcrumbs.length > 0 ? buildBreadcrumbSchema(breadcrumbs) : undefined), [breadcrumbs]);

    return (
        <HtmlHeadInclude>
            {/* eslint-disable-next-line react/no-danger */}
            <script
                type='application/ld+json'
                data-load-point='headEnd'
                dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }}
            />
            {breadcrumbSchema && (
                /* eslint-disable-next-line react/no-danger */
                <script
                    type='application/ld+json'
                    data-load-point='headEnd'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
                />
            )}
        </HtmlHeadInclude>
    );
};

export default ProductListHeadMetadata;
